import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import {
  PlaceAdditionalInfo,
} from './google-places.service';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface ViaCepResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
  /** Present and `true` when the CEP was not found. */
  erro?: boolean;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class ViaCepService {
  private readonly VIACEP_URL = 'https://viacep.com.br/ws';
  private readonly TIMEOUT_MS = 5_000;

  constructor(private http: HttpClient) {}

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Returns `true` when `value` (after stripping non-digits) is a valid
   * 8-digit Brazilian postal code.
   */
  isValidBrazilianCep(value: string): boolean {
    const digits = value.replace(/\D/g, '');
    return /^\d{8}$/.test(digits);
  }

  /**
   * Look up a Brazilian postal code using the ViaCEP public API.
   *
   * Returns `null` on any failure (network error, timeout, invalid CEP,
   * CEP not found). Never throws.
   */
  async searchByCep(cep: string): Promise<ViaCepResponse | null> {
    const digits = cep.replace(/\D/g, '');

    if (!this.isValidBrazilianCep(digits)) {
      return null;
    }

    try {
      const response = await firstValueFrom(
        this.http
          .get<ViaCepResponse>(`${this.VIACEP_URL}/${digits}/json/`)
          .pipe(timeout(this.TIMEOUT_MS)),
      );

      if (!response || response.erro) {
        return null;
      }

      return response;
    } catch {
      return null;
    }
  }

  /**
   * Map a ViaCEP response to a partial {@link PlaceAdditionalInfo}.
   * Only the fields that ViaCEP returns are populated; lat/lng and
   * `resolvedVia` are intentionally left to the caller.
   */
  mapToAddressFields(
    response: ViaCepResponse,
  ): Partial<PlaceAdditionalInfo> {
    return {
      postalCode: this.formatCep(response.cep),
      streetName: response.logradouro ?? '',
      neighborhood: response.bairro ?? '',
      city: response.localidade ?? '',
      state: response.uf ?? '',
      country: 'Brazil',
    };
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  /** Normalise CEP to the canonical `XXXXX-XXX` format. */
  private formatCep(cep: string): string {
    const digits = cep.replace(/\D/g, '');
    return digits.length === 8
      ? `${digits.slice(0, 5)}-${digits.slice(5)}`
      : cep;
  }
}
